import * as logClient from "@classdojo/log-client";
import { Button, DisplayText, HeadlineText } from "@classdojo/web/nessie";
import { RAW_cssValue } from "@classdojo/web/nessie/stylingLib";
import { useTranslation } from "@web-monorepo/i18next";
import { autoTranslate } from "@web-monorepo/vite-auto-translate-plugin/runtime";
import ToolkitAppScroller from "components/BeyondToolkit/ToolkitAppScroller";
import { IdeasShuffleIcon } from "components/IdeasIcons";
import ToolkitCard from "components/ToolkitCard";
import { useEffect, useMemo, useRef, useState } from "react";
import { logParentToolkitEvent } from "src/utils/logEvent";
import { generateLoadingAnimation, randomIndex } from "utils";
import { TABLE_TOPICS } from "utils/beyondToolkitTools";
import { sendUsage } from "utils/freemium";

const rolling = "/static/media/rolling-loop.mp3";
const finish = "/static/media/finish.mp3";

function TableTopics() {
  // this tells i18next to load these namespaces
  const { ready } = useTranslation(["dojo.beyond"]);
  const questions = useMemo(() => {
    return ready ? generateQuestions() : [];
  }, [ready]);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(() => randomIndex(questions));
  const [loading, setLoading] = useState(true);
  const [emojiIndex, setEmojiIndex] = useState(() => randomIndex(questions));
  const [showQuestion, setShowQuestion] = useState(true);

  const blinkQuestion = useRef<number | undefined>();
  const rollingAudioNode = useRef<HTMLAudioElement | null>(null);
  const finishAudioNode = useRef<HTMLAudioElement | null>(null);

  const newQuestion = () => {
    if (rollingAudioNode.current) {
      rollingAudioNode.current.play().catch(() => {
        logClient.sendMetrics([
          {
            type: "increment",
            value: 1,
            metricName: "toolkit.media.audio.play.error",
          },
        ]);
      });
    }
    setRandomQuestion();
    rerunAnimation();
    displayQuestion();
  };

  const updateEmoji = () => {
    setEmojiIndex(randomIndex(emojis));
  };

  const rerunAnimation = generateLoadingAnimation({
    numSegments: 6,
    endIntervalDuration: 100,
    startIntervalDuration: 300,
    onAnimationStateChange: setLoading,
    onAnimationTick: updateEmoji,
  });

  const setRandomQuestion = () => {
    setCurrentQuestionIndex(randomIndex(questions));
  };

  const displayQuestion = () => {
    setTimeout(() => {
      if (finishAudioNode.current) finishAudioNode.current.play();
      blinkQuestion.current = window.setInterval(() => {
        setShowQuestion((showQuestion) => !showQuestion);
      }, 200);
      window.setTimeout(() => {
        setShowQuestion(true);
        window.clearInterval(blinkQuestion.current);
        blinkQuestion.current = undefined;
      }, 800);
    }, 3000);
    // animation lasts for 3 seconds, so want to wait for that before displaying blinking text
  };

  useEffect(() => {
    if (ready) {
      newQuestion();
      sendUsage("dinnerTopics");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const { title, color, imgSrc } = TABLE_TOPICS();

  if (!ready) return null;

  return (
    <ToolkitAppScroller title={title} color={color} imgSrc={imgSrc}>
      <audio src={rolling} ref={rollingAudioNode} />
      <audio src={finish} ref={finishAudioNode} />
      <ToolkitCard>
        <div
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexGrow: 1,
            position: "relative",
          }}
        >
          {loading ? (
            <DisplayText>{emojis[emojiIndex]}</DisplayText>
          ) : (
            showQuestion && (
              <HeadlineText as="h2" level={2}>
                {questions[currentQuestionIndex]}
              </HeadlineText>
            )
          )}
        </div>
        {loading ? (
          <Button data-name="index:table_topics:toolkit.table_topics.shuffling" kind="secondary" disabled>
            Shuffling...
          </Button>
        ) : (
          <button
            data-name="index:table_topics:ideas_shuffle_icon"
            sx={{
              color: "dt_content_accent",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "transparent",
              border: "none",
              outlineColor: "transparent",
            }}
            onClick={() => {
              logParentToolkitEvent({
                eventName: "table_topics.new_question.tap",
              });
              newQuestion();
            }}
          >
            <IdeasShuffleIcon color="dt_content_accent" sx={{ marginRight: RAW_cssValue("5px") }} />
            New question
          </button>
        )}
      </ToolkitCard>
    </ToolkitAppScroller>
  );
}

export default TableTopics;

const generateQuestions = () => [
  autoTranslate("What are 3 words you would use to describe yourself?"),
  autoTranslate("What do you think you are good at?"),
  autoTranslate("Describe the best thing that happened to you in the past week."),
  autoTranslate("If you could make up a new subject at school, what would it be?"),
  autoTranslate("Share something that made you laugh this week."),
  autoTranslate("If you had one superpower what would it be? Why?"),
  autoTranslate("If someone gave you $50 what would you do with it?"),
  autoTranslate("If you could be any animal what would you be? Why?"),
  autoTranslate("What is something you would like to learn?"),
  autoTranslate("Share something you learned today."),
  autoTranslate("If you could design your own playground, what would it look like?"),
  autoTranslate("Describe your favorite game."),
  autoTranslate("Who is the nicest person you know?"),
  autoTranslate("If you could change one thing about your bedroom what would it be?"),
  autoTranslate("If you were stranded on a desert island and you could only have 1 item, what would it be? Why?"),
  autoTranslate("Would you rather eat your least favorite vegetable or have extra homework?"),
  autoTranslate("Share something you did this week that you want to do again."),
  autoTranslate("Which is better, being invisible or being able to fly? Why?"),
  autoTranslate("If we could only have one holiday, which one would you pick? Why?"),
  autoTranslate("When you are sad how do you cheer yourself up?"),
  autoTranslate("What is one thing you couldn’t live without. Why?"),
  autoTranslate("If you could go anywhere in the world, where would you go? Why?"),
  autoTranslate("What is the longest word you know how to spell?"),
  autoTranslate("When was the last time you told someone “thank you”? What was it for?"),
  autoTranslate("Share what you like most about one family member. "),
  autoTranslate("What is your favorite day of the week? Why?"),
  autoTranslate("Would you rather sneeze all day or have hiccups all day?"),
  autoTranslate("What is something you wish you could do right now?"),
  autoTranslate("If you could pick your own name, what would it be? Why?"),
  autoTranslate("When was the last time you gave someone a high-five? Who was it? What was it for?"),
  autoTranslate("Would you ever wear your clothes inside-out at school?"),
  autoTranslate("What is your biggest fear?"),
  autoTranslate("If you could only have one wish, what would it be? Why?"),
  autoTranslate("If you could play with only one thing your whole life, what would it be? Why?"),
  autoTranslate("What scares you the most? Why?"),
  autoTranslate("Can you remember a time when you had a really good day? What made it so good?"),
  autoTranslate("What do you usually do during recess?"),
  autoTranslate("If you could be great at something, what would you want to be great at? Why?"),
  autoTranslate("If you could be one age for the rest of your life, what age would you be? Why?"),
  autoTranslate("Would you rather take a picture or be in the picture?"),
  autoTranslate("What is your favorite joke?"),
  autoTranslate("If you could make up a holiday, what would it be called? What would you celebrate?"),
  autoTranslate("What is your favorite memory? Why?"),
  autoTranslate("What is your favorite book? What do you like about it?"),
  autoTranslate("If you could make one improvement to the world, what would it be? Why?"),
  autoTranslate("What is your funniest face?"),
  autoTranslate("What is your favorite thing about yourself?"),
  autoTranslate("Describe your perfect dessert."),
  autoTranslate("Who is the funniest person at the table?"),
  autoTranslate("What relative do you love seeing the most? Why?"),
  autoTranslate("If you could make a zoo, which animal would you have the most of?"),
  autoTranslate("When was the last time you helped your parents without being asked?"),
  autoTranslate("What makes someone a good friend?"),
  autoTranslate("What’s your favorite silly sound to make?"),
  autoTranslate("What has been the happiest day of your life?"),
  autoTranslate("If you could change one thing in the world, what would you change?"),
  autoTranslate("What is your favorite season? Why?"),
  autoTranslate("What cartoon character would you most like to be and why?"),
  autoTranslate("What is the best thing about being your age?"),
  autoTranslate("Describe your perfect day."),
  autoTranslate("Who is your best friend? Why are they your best friend?"),
  autoTranslate("What do you want to be when you grow up?"),
  autoTranslate("Is there anything you wish you had done differently today?"),
  autoTranslate("What is the best thing about being a part of this family?"),
  autoTranslate("Tell us something that made you happy today."),
  autoTranslate("Tell us something nice that you did for someone else today."),
  autoTranslate("If you could have any pet, what would you choose and why?"),
  autoTranslate("If you could be famous for a week, what would you be famous for and why?"),
  autoTranslate("If our family had a motto or slogan, what would it be?"),
  autoTranslate("If you could make a movie, what would it be about?"),
  autoTranslate("What are 3 things you would take if you had to leave your home in an emergency?"),
  autoTranslate("Share your favorite quality about each person at the table."),
  autoTranslate("What are two things you like about yourself and one thing you’d like to work on?"),
  autoTranslate("If you could outlaw one vegetable, what would it be?"),
  autoTranslate("Would you rather be the funniest person or the smartest person alive?"),
  autoTranslate("What is your favorite month? What do you love most about it?"),
  autoTranslate("If you had to eat the same dinner for the rest of your life, what would you eat?"),
  autoTranslate("If you could have lunch with anyone in the world, who would it be? What would you ask them?"),
  autoTranslate("What is your favorite song right now? What do you like most about it?"),
  autoTranslate("Would you rather be older or younger? Why?"),
  autoTranslate("Share something you admire about someone sitting at the table."),
  autoTranslate("What makes you feel loved?"),
  autoTranslate("What was your favorite part of today? Why?"),
  autoTranslate("If you could speak any language what would it be? Why?"),
  autoTranslate("What is your favorite part about school? What is your least favorite part?"),
  autoTranslate("What do you wish you did today that you didn’t do?"),
  autoTranslate("Describe your life 10 years from now."),
  autoTranslate("What is your favorite thing to do as a family?"),
  autoTranslate("What is one activity you wish our family could do this month?"),
  autoTranslate("Who is one of your heroes and why do they inspire you?"),
  autoTranslate("What is the scariest thing that ever happened to you?"),
  autoTranslate("Have you ever stood up for something that you thought was right?"),
  autoTranslate("What do children understand better than adults?"),
  autoTranslate("What makes someone brave?"),
  autoTranslate("If you could build your own house, what would it look like?"),
  autoTranslate("What’s something you are looking forward to in the next few weeks?"),
  autoTranslate("What makes a good morning for you?"),
  autoTranslate("What are you most proud of and why?"),
  autoTranslate("If you could go back in time, what would you do differently?"),
  autoTranslate("If you got a puppy today what would you name it?"),
];

const emojis = [
  "🍓",
  "🎉",
  "👻",
  "✌️",
  "🐶",
  "🐱",
  "🐝",
  "🐳",
  "🌵",
  "🍄",
  "🍁",
  "🌼",
  "⭐️",
  "🔥",
  "🌎",
  "⛄️",
  "❄️",
  "🍎",
  "🍉",
  "🐙",
  "🐼",
  "🐯",
  "🍿",
  "⚽️",
  "🏀",
  "🎨",
  "🎹",
  "🥁",
  "🚀",
  "🏝",
  "💎",
  "🎁",
  "📓",
  "✏️",
];
